import React from 'react';

/**
 * Two-panel picker: left = available tools, right = selected (ordered).
 * Props:
 *   allTools       – array of tool objects from the same machine (current tool excluded)
 *   selected       – array of tool IDs in display order
 *   onChange(ids)  – called with the new ordered ID array on every change
 */
const RecommendedToolsPicker = ({ allTools, selected, onChange }) => {
  const available = allTools.filter((t) => !selected.includes(t.id));
  const selectedTools = selected
    .map((id) => allTools.find((t) => t.id === id))
    .filter(Boolean);

  const add = (id) => onChange([...selected, id]);
  const remove = (id) => onChange(selected.filter((s) => s !== id));
  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...selected];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onChange(next);
  };
  const moveDown = (idx) => {
    if (idx === selected.length - 1) return;
    const next = [...selected];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onChange(next);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Recommended Tools</label>
      <div className="grid grid-cols-2 gap-3">

        {/* Left: available */}
        <div>
          <p className="text-xs text-gray-400 mb-1">Available</p>
          <div className="border border-gray-200 rounded-lg overflow-hidden min-h-[80px] max-h-52 overflow-y-auto">
            {available.length === 0 ? (
              <p className="text-xs text-gray-400 p-3">All tools added</p>
            ) : (
              available.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => add(t.id)}
                  className="w-full flex items-center justify-between px-3 py-2 text-left text-sm text-gray-700 hover:bg-dewalt/10 border-b border-gray-100 last:border-0 transition-colors"
                >
                  <span className="truncate">{t.name.uk}</span>
                  <svg className="flex-shrink-0 ml-2 text-gray-400" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right: selected + ordering */}
        <div>
          <p className="text-xs text-gray-400 mb-1">Selected (in order)</p>
          <div className="border border-gray-200 rounded-lg overflow-hidden min-h-[80px] max-h-52 overflow-y-auto">
            {selectedTools.length === 0 ? (
              <p className="text-xs text-gray-400 p-3">None selected</p>
            ) : (
              selectedTools.map((t, idx) => (
                <div
                  key={t.id}
                  className="flex items-center gap-1 px-2 py-1.5 border-b border-gray-100 last:border-0 bg-white"
                >
                  {/* Position badge */}
                  <span className="w-5 text-center text-xs font-semibold text-gray-400 flex-shrink-0">
                    {idx + 1}
                  </span>

                  {/* Name */}
                  <span className="flex-1 text-sm text-gray-700 truncate">{t.name.uk}</span>

                  {/* Up / Down */}
                  <div className="flex flex-col gap-0.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0}
                      className="p-0.5 rounded text-gray-400 hover:text-gray-700 disabled:opacity-25 transition-colors"
                      aria-label="Move up"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDown(idx)}
                      disabled={idx === selectedTools.length - 1}
                      className="p-0.5 rounded text-gray-400 hover:text-gray-700 disabled:opacity-25 transition-colors"
                      aria-label="Move down"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>

                  {/* Remove */}
                  <button
                    type="button"
                    onClick={() => remove(t.id)}
                    className="flex-shrink-0 p-0.5 rounded text-gray-300 hover:text-red-400 transition-colors"
                    aria-label="Remove"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default RecommendedToolsPicker;
